
import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import ObtieneDatosCookies from "../obtieneDatosCookiesUsuario";
import { MOTIVOS_NO_APTO_MEDICO } from "./motivosNoAptoMedico";

const API_EVALUACION =
  "https://wwwa.isepsantafe.net.ar/expedientes/ingresos/getEvaluacionItem";

const API_TOGGLE =
  "https://wwwa.isepsantafe.net.ar/expedientes/ingresos/togglePersonaItem";

// =====================================================
// RESULTADOS DISPONIBLES
// =====================================================

const OPCIONES = [
  {
    id: "completo_apto",
    nombre: "COMPLETO APTO",
    icono: "✅",
    color: "#16a34a",
    estado: "aprobado",
    resultado: "COMPLETO APTO",
  },
  {
    id: "completo_no_apto",
    nombre: "COMPLETO NO APTO",
    icono: "❌",
    color: "#dc2626",
    estado: "rechazado",
    resultado: "COMPLETO NO APTO",
  },
  {
    id: "con_faltantes",
    nombre: "CON FALTANTES",
    icono: "⚠️",
    color: "#f59e0b",
    estado: "cargado",
    resultado: "CON FALTANTES",
  },
  {
    id: "no_aporta_estudios",
    nombre: "NO APORTA ESTUDIOS",
    icono: "📁",
    color: "#64748b",
    estado: "rechazado",
    resultado: "NO APORTA ESTUDIOS",
  },
];

// Para evaluaciones sin observación configurada, mostrar solo APTO / NO APTO.
const OPCIONES_SIMPLES = [
  { id: "apto", nombre: "APTO", icono: "✅", color: "#16a34a", estado: "aprobado", resultado: "APTO" },
  { id: "no_apto", nombre: "NO APTO", icono: "❌", color: "#dc2626", estado: "rechazado", resultado: "NO APTO" },
];

const normalizar = (texto = "") =>
  String(texto || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const obtenerSeleccionGuardada = (evaluacion) => {
  if (!evaluacion) return null;

  const resultado = normalizar(evaluacion.resultado);
  const estado = normalizar(evaluacion.estado);

  // Primero reconocer resultados específicos.
  if (
    resultado.startsWith("no aporta estudios")
  ) {
    return "no_aporta_estudios";
  }

  if (
    resultado.startsWith("con faltantes")
  ) {
    return "con_faltantes";
  }

  if (
    resultado.startsWith("completo no apto")
  ) {
    return "completo_no_apto";
  }

  if (
    resultado.startsWith("completo apto")
  ) {
    return "completo_apto";
  }

  // Compatibilidad con evaluaciones anteriores.
  if (resultado === "apto") {
    return "completo_apto";
  }

  if (
    resultado.startsWith("no apto") ||
    estado === "rechazado"
  ) {
    return "completo_no_apto";
  }

  if (estado === "aprobado") {
    return "completo_apto";
  }

  // Un registro cargado puede ser solamente un archivo
  // del postulante. No se presupone intervención.
  return null;
};

const EvaluacionMedicaItem = ({
  datos,
  etapa,
  refres,
}) => {
  const Usuario = ObtieneDatosCookies();

  const [selected, setSelected] = useState(null);
  const [motivosNoApto, setMotivosNoApto] = useState([]);
  const [observacionAnterior, setObservacionAnterior] =
    useState("");
  const [observacionNueva, setObservacionNueva] =
    useState("");
  const [loading, setLoading] = useState(false);
  const [consultando, setConsultando] = useState(false);
  const [busquedaMotivo, setBusquedaMotivo] = useState("");

  const persona = datos?.persona;
  const item = datos?.item;

  const item_id =
    item?.id ||
    item?.item_id ||
    item?.idItem ||
    item;

  // Solo null o cadena vacía activan la evaluación simple.
  const soloAptoNoApto = item?.observacion === null || item?.observacion === "";
  const opcionesDisponibles = soloAptoNoApto ? OPCIONES_SIMPLES : OPCIONES;

  const tituloItem =
    item?.descripcion ||
    item?.nombre ||
    "Evaluación médica";

  const usuarioCarga = Usuario
    ? `${Usuario.identificacion || ""} ${
        Usuario.apellido || ""
      }, ${Usuario.nombre || ""}`.trim()
    : "sistema";

  const opcionSeleccionada = opcionesDisponibles.find(
    (opcion) => opcion.id === selected
  );

  const requiereMotivos =
    selected === "completo_no_apto";

  const requiereObservacion = [
    "completo_no_apto",
    "no_apto",
    "con_faltantes",
    "no_aporta_estudios",
  ].includes(selected);

  // =====================================================
  // CONSULTAR EVALUACIÓN EXISTENTE
  // =====================================================

  useEffect(() => {
    let activo = true;

    const cargarEvaluacion = async () => {
      if (
        !persona?.dni ||
        !item_id ||
        typeof item_id === "object"
      ) {
        return;
      }

      setConsultando(true);
      setSelected(null);
      setMotivosNoApto([]);
      setObservacionAnterior("");
      setObservacionNueva("");

      try {
        const res = await axios.post(
          API_EVALUACION,
          {
            dni: persona.dni,
            item_id,
          },
          {
            withCredentials: true,
          }
        );

        if (!activo) return;

        const evaluacion = res.data?.evaluacion;

        if (!evaluacion) {
          return;
        }

        const seleccionGuardada = obtenerSeleccionGuardada(evaluacion);
        setSelected(
          soloAptoNoApto
            ? seleccionGuardada === "completo_apto"
              ? "apto"
              : seleccionGuardada === "completo_no_apto"
              ? "no_apto"
              : null
            : seleccionGuardada
        );

        setObservacionAnterior(
          evaluacion.observaciones || ""
        );

        const resultadoGuardado = normalizar(
          evaluacion.resultado
        );

        const motivosDetectados =
          MOTIVOS_NO_APTO_MEDICO.filter((motivo) => {
            const codigo = normalizar(
              motivo.codigo
            );

            const descripcion = normalizar(
              motivo.descripcion
            );

            return (
              resultadoGuardado.includes(codigo) &&
              resultadoGuardado.includes(descripcion)
            );
          });

        setMotivosNoApto(motivosDetectados);
      } catch (error) {
        if (!activo) return;

        console.error(
          "Error consultando evaluación médica:",
          error
        );

        Swal.fire(
          "Error",
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            "No se pudo consultar la evaluación médica",
          "error"
        );
      } finally {
        if (activo) {
          setConsultando(false);
        }
      }
    };

    cargarEvaluacion();

    return () => {
      activo = false;
    };
  }, [persona?.dni, item_id, soloAptoNoApto]);

  // =====================================================
  // SELECCIONAR RESULTADO
  // =====================================================

  const seleccionarResultado = (id) => {
    if (loading) return;

    setSelected(id);

    if (id !== "completo_no_apto") {
      setMotivosNoApto([]);
      setBusquedaMotivo("");
    }
  };

  // =====================================================
  // MOTIVOS DE NO APTO
  // =====================================================

  const toggleMotivo = (motivo) => {
    setMotivosNoApto((prev) => {
      const existe = prev.some(
        (m) => m.codigo === motivo.codigo
      );

      if (existe) {
        return prev.filter(
          (m) => m.codigo !== motivo.codigo
        );
      }

      return [...prev, motivo];
    });
  };

  const motivosFiltrados =
    MOTIVOS_NO_APTO_MEDICO.filter((motivo) =>
      normalizar(
        `${motivo.codigo} ${motivo.descripcion}`
      ).includes(normalizar(busquedaMotivo))
    );

  // =====================================================
  // GUARDAR EVALUACIÓN
  // =====================================================

  const handleGuardar = async () => {
    if (!persona) {
      return Swal.fire(
        "Error",
        "No hay persona cargada",
        "error"
      );
    }

    if (
      !item_id ||
      typeof item_id === "object"
    ) {
      return Swal.fire(
        "Error",
        "El item_id no es válido",
        "error"
      );
    }

    if (!opcionSeleccionada) {
      return Swal.fire(
        "Atención",
        "Seleccioná un resultado médico",
        "warning"
      );
    }

    if (
      requiereObservacion &&
      !observacionNueva.trim()
    ) {
      return Swal.fire(
        "Atención",
        "Para este resultado debés ingresar una observación",
        "warning"
      );
    }

    const {
      estado,
      resultado: resultadoBase,
      nombre,
    } = opcionSeleccionada;

    const motivosTexto =
      requiereMotivos && motivosNoApto.length > 0
        ? motivosNoApto
            .map(
              (motivo) =>
                `${motivo.codigo} - ${motivo.descripcion}`
            )
            .join("\n")
        : requiereMotivos
        ? "Sin motivos seleccionados"
        : "No corresponde";

    // Guardamos un resultado que permite distinguir
    // las cuatro opciones, incluso si comparten estado.
    const resultado =
      requiereMotivos && motivosNoApto.length > 0
        ? `${resultadoBase} - ${motivosNoApto
            .map(
              (motivo) =>
                `${motivo.codigo} - ${motivo.descripcion}`
            )
            .join(" | ")}`
        : resultadoBase;

    const fechaCarga = new Date().toLocaleString(
      "es-AR",
      {
        timeZone:
          "America/Argentina/Buenos_Aires",
      }
    );

    // Se mantiene el formato de historial utilizado
    // por getPersonasPorEtapa para detectar intervenciones.
    const nuevaActualizacion = `
--- ACTUALIZACIÓN ---
Resultado: ${nombre}
Item: ${tituloItem}
Estado: ${estado}
Motivos NO APTO:
${motivosTexto}
Observación nueva: ${
      observacionNueva.trim() ||
      "Sin observación nueva"
    }
Usuario carga: ${usuarioCarga}
Fecha carga: ${fechaCarga}
`.trim();

    const observacionesFinales = [
      observacionAnterior.trim(),
      nuevaActualizacion,
    ]
      .filter(Boolean)
      .join("\n\n");

    // Confirmación con texto, sin interpretar HTML
    // proveniente de nombres u observaciones.
    const confirmacion = await Swal.fire({
      title: "¿Confirmar evaluación médica?",
      text: [
        `Persona: ${persona.apellido || ""}, ${
          persona.nombre || ""
        } (${persona.dni})`,
        `Ítem: ${tituloItem}`,
        `Resultado: ${nombre}`,
        `Estado: ${estado}`,
        `Motivos: ${motivosTexto}`,
        `Observación: ${
          observacionNueva.trim() ||
          "Sin observación nueva"
        }`,
        `Usuario: ${usuarioCarga}`,
      ].join("\n"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Cancelar",
      customClass: {
        popup: "evaluacion-medica-confirmacion",
      },
    });

    if (!confirmacion.isConfirmed) return;

    const payload = {
      persona_id:
        persona.id || persona.dni,

      proceso_id:
        etapa?.proceso_id ||
        etapa?.etapa_proceso_id ||
        datos?.proceso_id ||
        1,

      item_id: Number(item_id),

      tipo: "evaluacion",

      estado,
      resultado,
      observaciones: observacionesFinales,
    };

    try {
      setLoading(true);

      const res = await axios.post(
        API_TOGGLE,
        payload,
        {
          withCredentials: true,
        }
      );

      console.log(
        "Evaluación médica guardada:",
        res.data
      );

      setObservacionAnterior(
        observacionesFinales
      );

      setObservacionNueva("");

      await Swal.fire(
        "Guardado",
        `Se registró correctamente: ${nombre}`,
        "success"
      );

      if (typeof refres === "function") {
        refres();
      }
    } catch (error) {
      console.error(
        "Error guardando evaluación médica:",
        error
      );

      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error.message ||
          "No se pudo guardar la evaluación",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // RENDER
  // =====================================================

  if (!datos || !persona) {
    return <div>Cargando datos...</div>;
  }

  return (
    <div style={styles.container}>
      <h3>🩺 {tituloItem}</h3>

      <div style={styles.info}>
        <strong>Persona:</strong>{" "}
        {persona.apellido}, {persona.nombre}
        {" - DNI "}
        {persona.dni}
      </div>

      <hr />

      <h4>Resultado médico</h4>

      <p style={styles.descripcion}>
        Seleccioná el resultado correspondiente
        a la revisión de la documentación médica.
      </p>

      {/* ===========================================
          CUATRO RESULTADOS
          =========================================== */}

      <div style={styles.botonesEstado}>
        {opcionesDisponibles.map((opcion) => {
          const activo =
            selected === opcion.id;

          return (
            <button
              key={opcion.id}
              type="button"
              onClick={() =>
                seleccionarResultado(opcion.id)
              }
              disabled={loading || consultando}
              aria-pressed={activo}
              style={{
                ...styles.btnEstado,
                background: activo
                  ? opcion.color
                  : "#f1f5f9",
                color: activo
                  ? "#fff"
                  : "#334155",
                borderColor: activo
                  ? opcion.color
                  : "#cbd5e1",
                boxShadow: activo
                  ? `0 0 0 2px ${opcion.color}33`
                  : "none",
                opacity:
                  loading || consultando
                    ? 0.6
                    : 1,
              }}
            >
              <span style={{ fontSize: 20 }}>
                {opcion.icono}
              </span>

              <span>{opcion.nombre}</span>

              {activo && (
                <span style={styles.marca}>
                  ✓
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ===========================================
          RESUMEN DE SELECCIÓN
          =========================================== */}

      {opcionSeleccionada && (
        <div
          style={{
            ...styles.resumen,
            borderLeft: `5px solid ${opcionSeleccionada.color}`,
          }}
        >
          <strong>
            {opcionSeleccionada.icono}{" "}
            {opcionSeleccionada.nombre}
          </strong>

          <div style={{ marginTop: 5 }}>
            {selected === "completo_apto" &&
              "Documentación completa. Evaluación favorable."}

            {selected === "completo_no_apto" &&
              "Documentación completa. Evaluación desfavorable."}

            {selected === "con_faltantes" &&
              "Documentación revisada, con estudios o requisitos pendientes."}

            {selected === "no_aporta_estudios" &&
              "Se registra que el postulante no aporta los estudios requeridos."}
          </div>
        </div>
      )}

      {/* ===========================================
          MOTIVOS DE NO APTO
          =========================================== */}

      {requiereMotivos && (
        <>
          <h4>Motivos de NO APTO</h4>

          <div style={styles.buscadorInfo}>
            Los motivos son opcionales.
            Si no seleccionás ninguno, se
            guardará como COMPLETO NO APTO.
            La observación es obligatoria.
          </div>

          <div style={styles.opciones}>
            <input
              type="text"
              placeholder="Buscar por código o descripción..."
              value={busquedaMotivo}
              onChange={(e) =>
                setBusquedaMotivo(e.target.value)
              }
              style={styles.inputBusqueda}
            />

            <div style={styles.listaMotivos}>
              {motivosFiltrados.map((motivo) => {
                const checked =
                  motivosNoApto.some(
                    (m) =>
                      m.codigo === motivo.codigo
                  );

                return (
                  <label
                    key={motivo.codigo}
                    style={{
                      ...styles.checkItem,
                      background: checked
                        ? "#fee2e2"
                        : "#f8fafc",
                      borderColor: checked
                        ? "#dc2626"
                        : "#ddd",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        toggleMotivo(motivo)
                      }
                    />

                    <span>
                      <b>{motivo.codigo}</b>
                      {" - "}
                      {motivo.descripcion}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* ===========================================
          OBSERVACIONES ANTERIORES
          =========================================== */}

      {observacionAnterior && (
        <div style={styles.observacionAnterior}>
          <strong>
            📋 Observaciones anteriores:
          </strong>

          <pre style={styles.preObs}>
            {observacionAnterior}
          </pre>
        </div>
      )}

      {/* ===========================================
          NUEVA OBSERVACIÓN
          =========================================== */}

      <h4>
        Nueva observación / actualización
        {requiereObservacion && (
          <span style={{ color: "#dc2626" }}>
            {" *"}
          </span>
        )}
      </h4>

      <textarea
        disabled={loading || consultando}
        placeholder={
          selected === "con_faltantes"
            ? "Detallá qué estudios o requisitos faltan..."
            : selected === "no_aporta_estudios"
            ? "Indicá la situación de la documentación no presentada..."
            : selected === "completo_no_apto"
            ? "Detallá el motivo de la evaluación NO APTO..."
            : "Nueva observación / actualización..."
        }
        value={observacionNueva}
        onChange={(e) =>
          setObservacionNueva(e.target.value)
        }
        style={styles.textarea}
      />

      {requiereObservacion && (
        <p style={styles.aviso}>
          * Para este resultado es obligatorio
          registrar una observación.
        </p>
      )}

      {/* ===========================================
          GUARDAR
          =========================================== */}

      <button
        type="button"
        onClick={handleGuardar}
        disabled={
          !selected ||
          loading ||
          consultando
        }
        style={{
          ...styles.btnGuardar,
          opacity:
            !selected ||
            loading ||
            consultando
              ? 0.6
              : 1,
        }}
      >
        {loading
          ? "Guardando..."
          : consultando
          ? "Consultando..."
          : "GUARDAR EVALUACIÓN MÉDICA"}
      </button>
    </div>
  );
};

// =====================================================
// ESTILOS
// =====================================================

const styles = {
  container: {
    border: "1px solid #ddd",
    padding: 18,
    borderRadius: 10,
    maxWidth: 850,
    background: "#fff",
  },

  info: {
    background: "#f8fafc",
    padding: 10,
    borderRadius: 8,
    marginBottom: 10,
  },

  descripcion: {
    fontSize: 13,
    color: "#64748b",
    marginBottom: 12,
  },

  botonesEstado: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 10,
    marginBottom: 15,
  },

  btnEstado: {
    minHeight: 76,
    padding: "12px 10px",
    borderRadius: 9,
    border: "2px solid #ccc",
    cursor: "pointer",
    fontWeight: "bold",
    fontSize: 13,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    flexWrap: "wrap",
    transition: "all 0.2s ease",
  },

  marca: {
    fontWeight: "bold",
    fontSize: 18,
  },

  resumen: {
    padding: 12,
    background: "#f8fafc",
    borderRadius: 8,
    marginBottom: 16,
    fontSize: 13,
    color: "#334155",
  },

  buscadorInfo: {
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    padding: 10,
    borderRadius: 8,
    marginBottom: 10,
    fontSize: 13,
  },

  opciones: {
    marginBottom: 12,
  },

  inputBusqueda: {
    width: "100%",
    padding: 10,
    borderRadius: 8,
    border: "1px solid #ccc",
    marginBottom: 10,
    boxSizing: "border-box",
  },

  listaMotivos: {
    display: "grid",
    gap: 8,
    maxHeight: 320,
    overflowY: "auto",
    paddingRight: 5,
  },

  checkItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: 10,
    borderRadius: 8,
    border: "1px solid #ddd",
    cursor: "pointer",
  },

  observacionAnterior: {
    background: "#fef9c3",
    border: "1px solid #fde68a",
    padding: 10,
    borderRadius: 8,
    marginBottom: 15,
    fontSize: 13,
  },

  preObs: {
    whiteSpace: "pre-wrap",
    margin: 0,
    marginTop: 8,
    fontFamily: "inherit",
  },

  textarea: {
    width: "100%",
    minHeight: 110,
    padding: 10,
    borderRadius: 8,
    border: "1px solid #ccc",
    resize: "vertical",
    boxSizing: "border-box",
  },

  aviso: {
    fontSize: 12,
    color: "#b45309",
    marginTop: 6,
  },

  btnGuardar: {
    marginTop: 12,
    width: "100%",
    padding: "12px 15px",
    borderRadius: 8,
    border: "none",
    background: "#111827",
    color: "#fff",
    cursor: "pointer",
    fontWeight: "bold",
  },
};

export default EvaluacionMedicaItem;
